import {
    BaselineRange,
    CultureFitCategory,
    CultureFitCategoryId,
    WorkPreferenceBaselineScore,
} from '../../typings';
import { TFunction } from 'react-i18next';

export const calculateCompanyCategoryOrder = (
    baselineRange: BaselineRange,
): CultureFitCategoryId[] => {
    let categoryPriority: Record<CultureFitCategoryId, number> = {
        family: 1,
        hierarchy: 2,
        market: 3,
        adhocracy: 4,
    };
    const averages = Object.entries(baselineRange).map(([category, range]) => {
        const average = (range[0] + range[1]) / 2;
        return {
            category: category.toLowerCase() as CultureFitCategoryId,
            average,
        };
    });

    averages.sort((firstCategory, secondCategory) => {
        const averageDifference =
            secondCategory.average - firstCategory.average;
        if (averageDifference !== 0) {
            return averageDifference;
        }
        return (
            categoryPriority[firstCategory.category] -
            categoryPriority[secondCategory.category]
        );
    });

    return averages.map(({ category }) => category);
};

const orderCategoriesByCandidateHighestScore = (
    categories: CultureFitCategory[],
    baselineRange?: BaselineRange,
) => {
    let categoryPriority: Record<CultureFitCategoryId, number> = {
        family: 1,
        hierarchy: 2,
        market: 3,
        adhocracy: 4,
    };
    if (baselineRange && Object.keys(baselineRange).length) {
        const companyCategoryOrder =
            calculateCompanyCategoryOrder(baselineRange);
        if (companyCategoryOrder) {
            categoryPriority = companyCategoryOrder.reduce(
                (acc, category, index) => {
                    acc[category] = index + 1;
                    return acc;
                },
                {} as Record<CultureFitCategoryId, number>,
            );
        }
    }

    return categories.sort((firstCategory, secondCategory) => {
        const scoreDifference =
            secondCategory.candidate_score - firstCategory.candidate_score;
        if (scoreDifference !== 0) return scoreDifference;
        return (
            categoryPriority[firstCategory.id] -
            categoryPriority[secondCategory.id]
        );
    });
};

export const getCandidateHighestCultureCategory = (
    cultureFitCategories: CultureFitCategory[],
    baselineRange?: BaselineRange,
) => {
    return orderCategoriesByCandidateHighestScore(
        cultureFitCategories,
        baselineRange,
    )[0];
};

export const getWorkPreferencesFormattedData = (
    companyName: string,
    workPreferenceScores: CultureFitCategory[],
    t: TFunction,
    hasBaseline: boolean,
) => {
    const getCategoryScore = (
        scoreName: 'hierarchy' | 'market' | 'adhocracy' | 'family',
    ): number => {
        const catScore = workPreferenceScores.find(
            (scoreObj) => scoreObj.id === scoreName,
        );
        return catScore && catScore.candidate_score
            ? catScore.candidate_score
            : 0;
    };
    const getBaselineScore = (
        scoreName: 'hierarchy' | 'market' | 'adhocracy' | 'family',
    ): number | undefined => {
        const catScore = workPreferenceScores.find(
            (scoreObj) => scoreObj.id === scoreName,
        );
        return catScore && catScore.company_baseline !== undefined
            ? catScore.company_baseline
            : undefined;
    };
    const data: {
        name: string;
        Hierarchy: number | undefined;
        Market: number | undefined;
        Adhocracy: number | undefined;
        Family: number | undefined;
    }[] = [
        {
            name: t('CULTURAL_FIT.WORK_PREFERENCES.CANDIDATE_BAR_CHART_TITLE'),
            Hierarchy: getCategoryScore('hierarchy'),
            Market: getCategoryScore('market'),
            Adhocracy: getCategoryScore('adhocracy'),
            Family: getCategoryScore('family'),
        },
    ];

    if (hasBaseline) {
        const companyData = {
            name: t('CULTURAL_FIT.WORK_PREFERENCES.COMPANY_BAR_CHART_TITLE', {
                company_name: companyName,
            }),
            Hierarchy: getBaselineScore('hierarchy'),
            Market: getBaselineScore('market'),
            Adhocracy: getBaselineScore('adhocracy'),
            Family: getBaselineScore('family'),
        };

        const hasDefinedScore = [
            'Hierarchy',
            'Market',
            'Adhocracy',
            'Family',
        ].some(
            (category) =>
                companyData[category as keyof WorkPreferenceBaselineScore] !==
                undefined,
        );

        if (hasDefinedScore) {
            data.push(companyData);
        }
    }
    // Fix rounding error
    const adjustScores = (scoreData: WorkPreferenceBaselineScore) => {
        const categories: (keyof WorkPreferenceBaselineScore)[] = [
            'Family',
            'Adhocracy',
            'Hierarchy',
            'Market',
        ];
        const validScores = categories.filter(
            (category) => scoreData[category] !== undefined,
        );
        const sum = validScores.reduce(
            (acc, category) => acc + (scoreData[category] || 0),
            0,
        );

        if (sum > 100) {
            const minCategory = validScores.reduce(
                (min, category) =>
                    scoreData[category] < scoreData[min] ? category : min,
                validScores[0],
            );
            scoreData[minCategory] = scoreData[minCategory] - (sum - 100);
        } else if (sum < 100) {
            const maxCategory = validScores.reduce(
                (max, category) =>
                    scoreData[category] > scoreData[max] ? category : max,
                validScores[0],
            );
            scoreData[maxCategory] = scoreData[maxCategory] + (100 - sum);
        }
    };

    adjustScores(data[0] as WorkPreferenceBaselineScore);
    if (data.length > 1) {
        adjustScores(data[1] as WorkPreferenceBaselineScore);
    }

    return data;
};

export const getHighestCat = (
    categories: {
        Hierarchy: number | undefined;
        Market: number | undefined;
        Adhocracy: number | undefined;
        Family: number | undefined;
    },
    baselineRange?: BaselineRange,
): CultureFitCategoryId => {
    let fixedOrderCategory = ['family', 'hierarchy', 'market', 'adhocracy'];
    if (baselineRange && Object.keys(baselineRange).length) {
        fixedOrderCategory = calculateCompanyCategoryOrder(baselineRange);
    }

    let highestCategory = fixedOrderCategory[0];
    let highestScore = -Infinity;

    fixedOrderCategory.forEach((category) => {
        const score =
            categories[
                (category.charAt(0).toUpperCase() +
                    category.slice(1)) as keyof typeof categories
            ];
        if (score && score > highestScore) {
            highestCategory = category;
            highestScore = score;
        }
    });
    return highestCategory.toLowerCase() as CultureFitCategoryId;
};
